import React from 'react';
import { FileText, Leaf, Building2, FileCheck2, ArrowRight } from 'lucide-react';

const pillars = [
  {
    icon: Leaf,
    badge: 'CSRD · ESRS E1',
    title: 'Klimakennzahlen für Ihren Nachhaltigkeitsbericht',
    body:
      'Jede vermiedene kWh und jedes eingesparte Kilogramm CO₂ wird messbar dokumentiert — als belastbare Datenbasis für Ihren Lagebericht oder Sustainability-Statement nach ESRS-E1-Vorgaben.',
  },
  {
    icon: Building2,
    badge: 'EnEfG · § 8',
    title: 'Audit-Pflicht & Umsetzungspläne',
    body:
      'Wir liefern den prüfsicheren Maßnahmenkatalog gemäß Energie­effizienz­gesetz: identifizierte Maßnahmen, Wirtschaftlichkeit, Amortisations­dauer und CO₂-Wirkung — als anerkannte Grundlage Ihres Umsetzungs­plans.',
  },
  {
    icon: FileCheck2,
    badge: 'ISO 50001',
    title: 'Energie­management-konforme Doku',
    body:
      'TÜV-tauglicher Bericht inklusive Messsystem, Kalibrierungs­nachweis und energetischer Bewertung. Ihr Auditor übernimmt die Werte 1:1 — ohne Rückfragen, ohne Nacharbeit.',
  },
];

export default function ESGReporting() {
  return (
    <section
      id="esg"
      className="py-16 sm:py-24 md:py-32 relative bg-[#0f172a] text-white overflow-hidden"
      data-testid="esg-section"
    >
      {/* subtle grid backdrop */}
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#0891b2]/20 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 mb-14">
          <div className="lg:col-span-6">
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#0891b2] inline-flex items-center gap-2 mb-5">
              <span className="w-6 h-px bg-[#0891b2]" /> Für CFOs &amp; Nachhaltigkeits­manager
            </div>
            <h2 className="font-display font-black tracking-tighter leading-[1.02] text-3xl sm:text-4xl md:text-5xl">
              Aus Druckluft-Audit wird<br />
              <span className="text-[#0891b2]">ESG-Berichts­substanz.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-3">
            <p className="text-white/75 text-base sm:text-lg leading-relaxed">
              CSRD, EnEfG, ESRS E1 — die Berichts­pflichten verschärfen sich. Wir liefern Ihnen nicht
              nur einen Reparatur­katalog, sondern <strong className="text-white">prüf­fähige Klima- und Effizienz­kennzahlen</strong>,
              die <strong className="text-white">1:1 in Ihre Nachhaltigkeits- und Energie­berichte übernommen</strong> werden
              können. Jede gefundene Leckage wird zur dokumentierten Einsparung — in kWh, € und Kilogramm CO₂.
            </p>
          </div>
        </div>

        {/* Audit-Report mega card */}
        <div className="relative bg-white/[0.04] border border-white/10 rounded-sm p-7 sm:p-10 mb-4">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#0891b2] flex items-center gap-2 mb-4">
                <FileText className="w-3.5 h-3.5" /> Prüfsichere Audit-Dokumentation
              </div>
              <h3 className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tighter leading-[1.05] mb-4">
                Ein Audit. Belastbare Daten.<br />
                <span className="text-[#0891b2]">Für drei Berichts­standards.</span>
              </h3>
              <p className="text-white/65 text-[15px] leading-relaxed max-w-xl">
                Sie erhalten Ihre Leckage-Daten als <strong className="text-white">prüfsicheres PDF</strong> und
                strukturierten <strong className="text-white">Excel-Export</strong> — quantifiziert in m³/h, kWh, € und
                kg CO₂. Diese Werte übernimmt Ihr Wirtschafts­prüfer, Ihre Nachhaltigkeits­abteilung und Ihr
                Energie-Auditor 1:1 in den Lagebericht, ESG-Statement oder Umsetzungs­plan.
              </p>
              <a
                href="#kontakt"
                className="inline-flex items-center gap-2 mt-6 text-[#0891b2] hover:text-white transition-colors font-medium text-sm border-b border-[#0891b2]/40 pb-1"
                data-testid="esg-cta"
              >
                Reporting-Anforderungen besprechen <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            <div className="md:col-span-5">
              <div className="space-y-2.5 font-mono-tech text-xs">
                {[
                  ['CSRD-Lagebericht', 'Datenbasis ESRS E1'],
                  ['EnEfG-Umsetzungs­plan', '§ 8 · maßnahmen­basiert'],
                  ['ISO 50001 Auditbeleg', 'energetische Bewertung'],
                  ['CO₂-Einsparung', 'kg CO₂ · jahres­gemittelt'],
                  ['Export-Formate', 'PDF · XLSX'],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 py-2 border-b border-white/10">
                    <span className="text-white/85">{k}</span>
                    <span className="text-[#0891b2] text-right">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Three pillars */}
        <div className="grid md:grid-cols-3 gap-4">
          {pillars.map((p) => {
            const Icon = p.icon;
            return (
              <article
                key={p.badge}
                className="bg-white/[0.03] border border-white/10 rounded-sm p-7 hover:border-[#0891b2]/40 transition-colors"
                data-testid={`esg-pillar-${p.badge.split(' ')[0].toLowerCase()}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="w-10 h-10 border border-[#0891b2]/40 bg-[#0891b2]/10 grid place-items-center">
                    <Icon className="w-4 h-4 text-[#0891b2]" strokeWidth={2} />
                  </div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#0891b2]">
                    {p.badge}
                  </div>
                </div>
                <h3 className="font-display font-bold text-white text-lg sm:text-xl tracking-tight leading-tight mb-3">
                  {p.title}
                </h3>
                <p className="text-white/65 text-sm leading-relaxed">{p.body}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
